import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { LeadsService, LeadsResponse, UiLead } from 'src/app/api/leads.service';
import { IconService } from 'src/app/ui/icon.service';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { AssignLeadComponentInputs } from '../assign-lead/assign-lead.component';
import { DeleteLeadComponentInputs } from '../delete-lead/delete-lead.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Column } from 'src/app/ui';
import { LocalAuthService } from 'src/app/auth/local-auth.service';

@Component({
  selector: 'app-web-leads',
  templateUrl: './web-leads.component.html',
  styleUrls: ['./web-leads.component.scss'],
  standalone: false
})
export class WebLeadsComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  nonTransferredLeads = signal<UiLead[]>([]);
  transferredLeads = signal<UiLead[]>([]);
  inactiveLeads = signal<UiLead[]>([]);

  // Role-based access control - only salesAdmin role can access web leads
  canAccessWebLeads = computed(() => {
    return this.auth.hasRole('salesAdmin') || this.auth.hasRole('it');
  });

  newLeadscolumns: Column<UiLead>[] = [
    { field: 'id', header: 'ID', type: 'text', sortable: true, filterable: true },
    { field: 'timestampFormatted', header: 'Received On', type: 'text', sortable: true, filterable: true },
    { field: 'officeName', header: 'Office', type: 'text', sortable: true, filterable: true },
    { field: 'size', header: 'Size', type: 'text', sortable: true, filterable: true },
    { field: 'name', header: 'Name', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: true },
    { field: 'org', header: 'Organization', type: 'text', sortable: true, filterable: true },
    { field: 'email', header: 'Email', type: 'text', sortable: true, filterable: true },
    { field: 'phone', header: 'Phone', type: 'phone', sortable: true, filterable: true },
    { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'comments', header: 'Comments', type: 'html', sortable: true, filterable: true },
    { field: 'securityScore', header: 'Security Score', type: 'number', pipeParams: '1.0-2', sortable: true, filterable: true }
  ];

  transferredLeadsColumns: Column<UiLead>[] = [
    { field: 'id', header: 'ID', type: 'text', sortable: true, filterable: true },
    { field: 'officeName', header: 'Office', type: 'text', sortable: true, filterable: true },
    { field: 'groupName', header: 'Group', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: true },
    { field: 'assignedOnFormatted', header: 'Assigned On', type: 'text', sortable: true, filterable: true },
    { field: 'recruiterName', header: 'Recruiter', type: 'text', sortable: true, filterable: true },
    { field: 'size', header: 'Size', type: 'text', sortable: true, filterable: true },
    { field: 'name', header: 'Name', type: 'text', sortable: true, filterable: true },
    { field: 'org', header: 'Organization', type: 'text', sortable: true, filterable: true },
    { field: 'email', header: 'Email', type: 'text', sortable: true, filterable: true },
    { field: 'phone', header: 'Phone', type: 'phone', sortable: true, filterable: true },
    { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'comments', header: 'Comments', type: 'html', sortable: true, filterable: true },
    { field: 'securityScore', header: 'Security Score', type: 'number', pipeParams: '1.0-2', sortable: true, filterable: true }
  ];

  inactiveLeadsColumns: Column<UiLead>[] = [
    { field: 'id', header: 'ID', type: 'text', sortable: true, filterable: true },
    { field: 'officeName', header: 'Office', type: 'text', sortable: true, filterable: true },
    { field: 'timestampFormatted', header: 'Received On', type: 'text', sortable: true, filterable: true },
    { field: 'size', header: 'Size', type: 'text', sortable: true, filterable: true },
    { field: 'name', header: 'Name', type: 'text', sortable: true, filterable: true },
    { field: 'org', header: 'Organization', type: 'text', sortable: true, filterable: true },
    { field: 'email', header: 'Email', type: 'text', sortable: true, filterable: true },
    { field: 'phone', header: 'Phone', type: 'phone', sortable: true, filterable: true },
    { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'comments', header: 'Comments', type: 'html', sortable: true, filterable: true },
    { field: 'securityScore', header: 'Security Score', type: 'number', pipeParams: '1.0-2', sortable: true, filterable: true }
  ];

  newLeadsFilterFields(): string[] {
    return this.newLeadscolumns.map(col => String(col.field));
  }

  transferredLeadsFilterFields(): string[] {
    return this.transferredLeadsColumns.map(col => String(col.field));
  }

  inactiveLeadsFilterFields(): string[] {
    return this.inactiveLeadsColumns.map(col => String(col.field));
  }

  constructor(
    private leadsService: LeadsService, 
    public icons: IconService,
    public dataPadService: DataPadService,
    private auth: LocalAuthService
  ) {}

  ngOnInit() {
    // Only load leads if user has proper access
    if (this.canAccessWebLeads()) {
      this.loadLeads();
    }
  }

  loadLeads() {
    this.leadsService.getLeads()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: LeadsResponse) => {
        console.log(response, 'response')
        this.nonTransferredLeads.set(response.nonTransferredLeads);
        this.transferredLeads.set(response.transferredLeads);
        this.inactiveLeads.set(response.inactiveLeads);
      });
  }

  assignLead(lead: UiLead) {
    this.dataPadService.showPanel<AssignLeadComponentInputs>({
      panelName: 'assignLead',
      inputData: {
        lead: lead
      },
      outputs: {
        assignConfirmed: (result: boolean) => this.loadLeads()
      }
    });
  }

  deleteLead(leadId: number) {
    this.dataPadService.showPanel({ 
      panelName: "deleteLead", 
      inputData: { leadId: leadId }, 
      outputs: { 
        deleteConfirmed: (result: boolean) => this.loadLeads() 
      } 
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 